// @react/providers/StoreProvider.tsx
// Auto-generated by corebuilder. DO NOT EDIT.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeCore, disposeCore, rootStore, type RootStore } from '@core';

const StoreContext = createContext<RootStore | null>(null);

export interface StoreProviderProps {
  children: React.ReactNode;
  apiBaseUrl?: string;
  defaultLocale?: string;
  scope?: { platformId?: string; productId?: string; tenantId?: string };
  themeColors?: import('@core/stores/theme').ThemeConfig;
  onInitialized?: () => void;
  onError?: (error: Error) => void;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode | ((error: Error) => React.ReactNode);
}

/**
 * Provider component that initializes @core and provides MobX stores to the app.
 * Must wrap your application root.
 *
 * @example
 * ```tsx
 * import { StoreProvider } from '@react/providers';
 *
 * function App() {
 *   return (
 *     <StoreProvider
 *       apiBaseUrl={import.meta.env.VITE_API_URL}
 *       defaultLocale="en"
 *       onInitialized={() => console.log('Core initialized')}
 *     >
 *       <Router>
 *         <Routes />
 *       </Router>
 *     </StoreProvider>
 *   );
 * }
 * ```
 */
export function StoreProvider({
  children,
  apiBaseUrl,
  defaultLocale,
  scope,
  themeColors,
  onInitialized,
  onError,
  loadingComponent,
  errorComponent,
}: StoreProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        await initializeCore({
          apiBaseUrl: apiBaseUrl || import.meta.env.VITE_API_URL || '/api',
          defaultLocale,
          themeColors,
          scope,
        });

        if (mounted) {
          setIsInitialized(true);
          onInitialized?.();
        }
      } catch (err) {
        if (mounted) {
          const initError = err instanceof Error ? err : new Error(String(err));
          setError(initError);
          onError?.(initError);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      disposeCore();
    };
  }, [apiBaseUrl, defaultLocale, scope, themeColors, onInitialized, onError]);

  if (error) {
    if (errorComponent) {
      return typeof errorComponent === 'function'
        ? <>{errorComponent(error)}</>
        : <>{errorComponent}</>;
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        color: '#dc2626',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
          Initialization Error
        </h1>
        <pre style={{
          background: '#fef2f2',
          padding: '1rem',
          borderRadius: '0.5rem',
          overflow: 'auto',
          maxWidth: '100%',
        }}>
          {error.message}
        </pre>
      </div>
    );
  }

  if (!isInitialized) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif',
      }}>
        Loading...
      </div>
    );
  }

  return (
    <StoreContext.Provider value={rootStore}>
      {children}
    </StoreContext.Provider>
  );
}

/**
 * Hook to access the root store containing all service stores.
 * Must be used within a StoreProvider.
 *
 * @example
 * ```tsx
 * import { observer } from 'mobx-react-lite';
 * import { useRootStore } from '@react/providers';
 *
 * const Dashboard = observer(() => {
 *   const { auth, user } = useRootStore();
 *
 *   if (!auth.isAuthenticated) {
 *     return <Navigate to="/login" />;
 *   }
 *
 *   return <div>Welcome, {auth.user?.name}</div>;
 * });
 * ```
 */
export function useRootStore(): RootStore {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useRootStore must be used within a StoreProvider');
  }
  return store;
}

/**
 * Hook to access a specific store from the root store.
 *
 * @example
 * ```tsx
 * import { observer } from 'mobx-react-lite';
 * import { useStore } from '@react/providers';
 *
 * const UserProfile = observer(() => {
 *   const auth = useStore(store => store.auth);
 *   return <div>{auth.user?.email}</div>;
 * });
 * ```
 */
export function useStore<T>(selector: (store: RootStore) => T): T {
  const store = useRootStore();
  return selector(store);
}
